<template>
  <div>
    <!-- over-the-air bundle update -->
    <el-dialog
      :model-value="isMandatoryUpdateReady"
      width="580px"
      top="9vh"

      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      modal-class="bg-light [&_.el-dialog\_\_body]:pt-0 [&_.el-dialog\_\header]:px-5"
    >
      <template #header>
        <div class="DialogTitle">
          <h4 class="text-primary-500">
            <span>{{ $t('CapacitorOtaUpdate.title') }}</span>
          </h4>
        </div>
      </template>

      <div class="text-base px-5">
        <p class="mb-4">
          {{ $t('CapacitorOtaUpdate.message') }}
        </p>
      </div>

      <span class="flex items-center justify-end px-5 mt-8">
        <el-button
          size="large"
          type="primary"
          class="px-5 w-full sm:w-auto m-0"
          @click="applyUpdate"
        >
          {{ $t('CapacitorOtaUpdate.restart') }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script setup>
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { LiveUpdate } from '@capawesome/capacitor-live-update';

// In `ios:dev` the webview is pointed at the local dev server, so the bundle the
// plugin manages is not the one being displayed. Every route into
// `checkForUpdate()` is gated on this, so the function itself needs no guard.
const isOtaSupported = !import.meta.dev && Capacitor.getPlatform() === 'ios';

const isMandatoryUpdateReady = ref(false);
// `appStateChange` and the end-of-call watcher can both fire a check within the
// same tick, which would download the same bundle twice.
let isChecking = false;

// Downloading several MB competes with the OpenTok/Twilio streams, and reloading
// mid-call would drop the call outright — both wait until the user is free.
const isCallActive = computed(() => {
  const video = useVideoStore();
  const calling = useOperatorCallingStore();

  return Boolean(video.isActive || video.isOpen || calling.callSid || calling.token);
});

const reportToSentry = (readyResult, nativeVersion, currentBundleId) => {
  const sentry = useNuxtApp().$sentry;

  if (!sentry) {
    return;
  }

  const bundleId = currentBundleId || 'default';
  const scope = sentry.getCurrentScope();

  // A tag (unlike an extra) is searchable, which is what makes it possible to
  // tell "this crash came from an OTA bundle" apart from a store release.
  scope.setTag('ota.bundleId', bundleId);
  scope.setExtra('release', `${nativeVersion}+${bundleId}`);

  if (readyResult?.rollback) {
    sentry.captureMessage(`[OTA] Rolled back from bundle ${readyResult.previousBundleId}`, 'warning');
  }
};

const checkForUpdate = async(state) => {
  if (!state?.isActive || isCallActive.value || isChecking) {
    return;
  }

  isChecking = true;

  const manifest = await useOtaUpdate.check().catch((error) => {
    console.error('[OTA] Update check failed', error);
    useNuxtApp().$sentry?.captureException(error);
    return null;
  }).finally(() => {
    isChecking = false;
  });

  if (!manifest) {
    return;
  }

  // The guard at the top of this function only covers the *start* of the check.
  // A call that begins while the bundle downloads would otherwise be met with a
  // restart prompt, and restarting drops the call. The watcher below re-runs the
  // check on hangup; the bundle is downloaded by then, so this only defers the
  // prompt. (The download itself cannot be aborted — the plugin has no API for
  // it — so a call starting mid-download does compete with it for bandwidth.)
  if (manifest.mandatory === true && !isCallActive.value) {
    isMandatoryUpdateReady.value = true;
  }
};

const applyUpdate = async() => {
  isMandatoryUpdateReady.value = false;
  await LiveUpdate.reload();
};

onMounted(async() => {
  if (!isOtaSupported) {
    return;
  }

  const readyResult = await useNuxtApp().$otaReady;
  const [{ bundleId }, { version }] = await Promise.all([LiveUpdate.getCurrentBundle(), App.getInfo()]);

  useUIStore().liveUpdate = {
    currentBundleId: bundleId,
    nativeVersion: version,
  };

  // Printed on every launch: the first thing worth knowing about a reported
  // problem is which bundle the device was actually running, and the native
  // version alone does not say that.
  console.log(`[App] iOS ${version} · bundle ${bundleId || 'App Store'} · built ${useRuntimeConfig().public.buildDate}`);

  reportToSentry(readyResult, version, bundleId);

  // An App Store upgrade leaves the previous release's bundle in place, so the
  // new binary would keep serving old JavaScript indefinitely. Dropping it
  // reloads the webview, which ends this context — nothing below would run.
  const isReloading = await useOtaUpdate.resetOnNativeUpgrade(version, bundleId).catch((error) => {
    console.error('[OTA] Could not drop the bundle after a native upgrade', error);
    return false;
  });

  if (isReloading) {
    return;
  }

  checkForUpdate({ isActive: true });

  App.addListener('appStateChange', checkForUpdate);
});

// A check skipped because the user was on a call is retried once they hang up.
// Registered here rather than in `onMounted`, which is async: past its first
// `await` the active effect scope is gone and the watcher would outlive the
// component. Gating the registration is what keeps an `ios:dev` build from
// fetching the production manifest the moment a call ends.
if (isOtaSupported) {
  watch(isCallActive, (isActive) => {
    if (!isActive) {
      checkForUpdate({ isActive: true });
    }
  });
}
</script>
